import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import { match } from 'ts-pattern';

import { DEFAULT_FORMAT_SETTINGS } from '@nesso/session-contracts/config';
import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';

import { PermissionGate } from '@/entities/practice-member';
import type { Session } from '@/entities/session';
import {
    DEFAULT_NOTE_TYPE,
    type SessionNoteSettings,
    useGenerateNoteOptions,
    useIsGenerationAvailable,
} from '@/entities/session-note';
import { TooltipButton } from '@/shared/ui/common/tooltip-button';

import { useSettingsState } from '../model/use-settings-state';
import { EditSessionNoteSettingsContent } from './edit-session-note-settings/edit-session-note-settings-content';
import { EditSessionNoteSettingsDialog } from './edit-session-note-settings/edit-session-note-settings-dialog';
import { EditSessionNoteSettingsPopover } from './edit-session-note-settings/edit-session-note-settings-popover';

type Props = {
    session: Session;
};

export function EditSessionNoteSettings({ session }: Props) {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const isXs = useBreakpointDown('xs');
    const sessionId = session.id;
    const generateNoteMutation = useMutation(useGenerateNoteOptions(sessionId));
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);
    const settings: SessionNoteSettings =
        session.currentNote?.generator?.settings ?? DEFAULT_FORMAT_SETTINGS;
    const type = session.currentNote?.type ?? DEFAULT_NOTE_TYPE;

    const updateSettings = (newSettings: SessionNoteSettings) => {
        generateNoteMutation.mutate({ sessionId, settings: newSettings, type });
    };

    const {
        settings: localSettings,
        update,
        save,
        reset,
    } = useSettingsState(settings, updateSettings);

    return (
        <PermissionGate
            requiredAccessSources={['CLINICIAN', 'NOTES_BASE', 'NOTES_ACTIVE']}
            fallback={(missing) => (
                <TooltipButton
                    variant="outline"
                    size="icon"
                    aria-label="Customize Note"
                    disabled
                    tooltip={match(missing)
                        .when(
                            (m) => m.includes('NOTES_BASE'),
                            () => 'Add Notes to your plan to edit your notes',
                        )
                        .when(
                            (m) => m.includes('NOTES_ACTIVE'),
                            () => 'Update billing information to edit your notes',
                        )
                        .otherwise(() => 'This is currently unavailable')}
                >
                    <Settings2 className="w-4 h-4" />
                </TooltipButton>
            )}
        >
            {isXs ? (
                <EditSessionNoteSettingsDialog
                    open={settingsOpen}
                    onOpenChange={setSettingsOpen}
                    disabled={!isGenerationAvailable}
                    save={save}
                    reset={reset}
                >
                    <EditSessionNoteSettingsContent
                        type={type}
                        settings={localSettings}
                        onChange={update}
                    />
                </EditSessionNoteSettingsDialog>
            ) : (
                <EditSessionNoteSettingsPopover
                    open={settingsOpen}
                    disabled={!isGenerationAvailable}
                    onOpenChange={setSettingsOpen}
                    save={save}
                    reset={reset}
                >
                    <EditSessionNoteSettingsContent
                        type={type}
                        settings={localSettings}
                        onChange={update}
                    />
                </EditSessionNoteSettingsPopover>
            )}
        </PermissionGate>
    );
}
